import React, {
  ComponentClass,
  ComponentType,
  FC,
  ReactElement,
  Suspense,
  useEffect,
} from 'react';
import { connect, ConnectedComponent } from 'react-redux';
import {
  Switch,
  Route,
  Redirect,
  withRouter,
  RouteComponentProps,
  useHistory,
} from 'react-router-dom';
import { pick } from 'lodash';
import MetaTags from '../components/MetaTags';
import Loading from '../components/Loading';
import { useLanguage } from '../locale/useLanguage';
import { TranslationKeys } from '../locale/localizationContext';
import { useAppDispatch } from '../redux/core';
import {
  addRouteToHistory,
  removeLastRouteFromHistory,
  replaceLastRouteFromHistory,
} from '../redux/general/general.actions';

export const type = {
  PUBLIC: 'PUBLIC',
  SECURED: 'SECURED',
  AUTHENTICATION: 'AUTHENTICATION',
};

export type RouteType = {
  id: string | number;
  path: string;
  type?: string;
  icon?:
    | ReactElement
    | ConnectedComponent<any, any>
    | ComponentClass
    | ComponentType
    | string;
  exact?: boolean;
  redirectTo?: string;
  redirectToExternal?: string;
  children?: Array<any>;
  titleKey: string;
  component?: any;
  noFollow?: boolean;
  layout?: any;
  layoutOptions?: object;
  scopes?: Array<string>;
  showInMenu?: boolean;
};
export const getFlatRouteList = (routes: Array<RouteType>) => {
  const routeList: Array<RouteType> = [];
  routes.forEach((route) => {
    if (route.path) {
      routeList.push(route);
    }
    if (route.children && route.children.length > 0) {
      route.children.forEach((child) => {
        child.parent_id = route.id;
        if (child.path) {
          routeList.push(child);
        }
      });
    }
  });
  return routeList;
};

type RouteComponentType = {
  route: RouteType;
} & Record<string | number, any>;

const RouteComponent: React.FC<RouteComponentType> = ({ route, ...props }) => {
  const [t] = useLanguage();

  const title = t(route.titleKey as TranslationKeys<string>);

  return (
    <>
      <MetaTags title={title} noFollow={route.noFollow} />
      <Suspense fallback={<Loading />}>
        {route.layout ? (
          <route.layout
            {...{
              ...props,
              ...pick(route, [
                'id',
                'parent_id',
                'path',
                'title',
                'showInMenu',
              ]),
              ...route.layoutOptions,
            }}
          >
            <route.component {...props} />
          </route.layout>
        ) : (
          <route.component {...props} />
        )}
      </Suspense>
    </>
  );
};
const Router: FC<
  {
    routes: Array<RouteType> | null;
    authorized: boolean;
  } & RouteComponentProps
> = ({ routes = null, authorized = false }) => {
  const history = useHistory();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (history.action === 'POP') {
      dispatch(removeLastRouteFromHistory());
    } else if (history.action === 'PUSH') {
      dispatch(addRouteToHistory(history.location.pathname));
    } else {
      dispatch(replaceLastRouteFromHistory(history.location.pathname));
    }
  }, [dispatch, history.action, history.location.pathname]);

  return routes ? (
    <>
      <Switch>
        {getFlatRouteList(routes).map((r: any, i) => (
          <Route
            key={i}
            path={r.path}
            exact={r.exact || true}
            render={(props) => {
              if (r.redirectToExternal) {
                window.location = r.redirectToExternal;
                return <Loading />;
              } else if (r.redirectTo) {
                return <Redirect to={r.redirectTo} />;
              } else if (!authorized && r.type === type.SECURED) {
                return <Redirect to={'/sign-in'} />;
              } else if (authorized && r.type === type.AUTHENTICATION) {
                return <Redirect to={'/'} />;
              } else {
                if (!r.component) {
                  return <Loading />;
                } else {
                  return <RouteComponent route={r} {...props} />;
                }
              }
            }}
          />
        ))}

        <Route key={-1} render={() => <Redirect to={'/'} />} />
      </Switch>
    </>
  ) : (
    <p>No routes</p>
  );
};
export default withRouter(connect()(Router));
